import { supabase } from "@/integrations/supabase/client";

/**
 * Coordonnées de contact d'une annonce de service.
 *
 * Ces données personnelles (email / téléphone du vendeur) ne sont plus stockées
 * sur la fiche produit publique : elles vivent dans `product_contacts`, dont la
 * RLS ne les expose qu'au vendeur, à ses acheteurs et aux administrateurs.
 */
export interface ProductContact {
  contactEmail: string | null;
  contactPhone: string | null;
}

const EMPTY_CONTACT: ProductContact = { contactEmail: null, contactPhone: null };

/**
 * Lit les coordonnées d'une annonce. Retourne des valeurs vides si l'utilisateur
 * n'y a pas droit : l'absence de contact ne doit jamais bloquer l'affichage.
 */
export async function fetchProductContact(productId: string): Promise<ProductContact> {
  const { data, error } = await supabase
    .from("product_contacts")
    .select("contact_email, contact_phone")
    .eq("product_id", productId)
    .maybeSingle();

  if (error || !data) return EMPTY_CONTACT;

  return {
    contactEmail: data.contact_email ?? null,
    contactPhone: data.contact_phone ?? null,
  };
}

/**
 * Crée ou met à jour les coordonnées d'une annonce (réservé au vendeur par RLS).
 */
export async function saveProductContact(
  productId: string,
  contact: ProductContact,
): Promise<void> {
  const { error } = await supabase.from("product_contacts").upsert(
    {
      product_id: productId,
      contact_email: contact.contactEmail || null,
      contact_phone: contact.contactPhone || null,
    },
    { onConflict: "product_id" },
  );

  if (error) throw error;
}

/** Supprime les coordonnées (annonce qui n'est plus un service). */
export async function deleteProductContact(productId: string): Promise<void> {
  await supabase.from("product_contacts").delete().eq("product_id", productId);
}
